
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ArrowLeft, Calendar } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import HistoryList from "@/components/history/HistoryList";
import HistoryDetails from "@/components/history/HistoryDetails";
import type { Json } from "@/integrations/supabase/types";
import { useAuth } from "@/hooks/useAuth";

interface AssessmentHistory {
  id: string;
  session_id: string;
  questions_answers: Json;
  result: Json;
  completed_at: string;
}

const History = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [histories, setHistories] = useState<AssessmentHistory[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedHistory, setSelectedHistory] = useState<AssessmentHistory | null>(null);

  useEffect(() => {
    if (!authLoading) {
      if (!user) {
        navigate('/auth');
        return;
      }
      fetchHistories();
    }
  }, [user, authLoading, navigate]);

  const fetchHistories = async () => {
    if (!user) return;
    
    try {
      const { data, error } = await supabase
        .from('assessment_histories')
        .select('*')
        .eq('user_id', user.id)
        .order('completed_at', { ascending: false });

      if (error) {
        console.error('Error fetching histories:', error);
      } else {
        setHistories(data || []);
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading your assessment history...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-background/80 backdrop-blur-sm border-b border-border">
        <div className="flex items-center justify-between px-4 py-3">
          <Button 
            variant="ghost" 
            onClick={() => navigate("/")}
            className="flex items-center space-x-2"
          >
            <ArrowLeft className="h-4 w-4" />
            <span>Back</span>
          </Button>
          <div className="text-center">
            <h1 className="text-lg font-bold text-foreground">History</h1>
            <p className="text-xs text-muted-foreground">Your assessments</p>
          </div>
          <div className="w-16"></div>{/* Spacer for center alignment */}
        </div>
      </header>

      <main className="px-4 py-6 space-y-6">
        {histories.length === 0 ? (
          <Card className="p-6 text-center">
            <Calendar className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h2 className="text-lg font-semibold text-foreground mb-2">No Assessments Yet</h2>
            <p className="text-muted-foreground mb-4">You haven't completed any health assessments.</p>
            <Button onClick={() => navigate("/assessment")}>
              Take Your First Assessment
            </Button>
          </Card>
        ) : (
          <div className="space-y-4">
            <HistoryList
              histories={histories}
              selectedHistory={selectedHistory}
              onSelectHistory={setSelectedHistory}
            />
            {selectedHistory && (
              <HistoryDetails selectedHistory={selectedHistory} />
            )}
          </div>
        )}
      </main>
    </div>
  );
};

export default History;
